"use client";

import clsx from "clsx";
import dynamic from "next/dynamic";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@/components/library/Button";
import Icon, {IconNameExtended} from "@/components/library/Icon";
import Input from "@/components/library/Input";
import { AgencyDirectoryPhoneType, AgencyDirectoryTransferType } from "@/enums/agencies";
import { useAlert } from "@/hooks/useAlert";
import { useOrg } from "@/hooks/useOrg";
import { AgencyDirectoryContact, AgencyDirectoryContactPhone } from "@/types/agencies";
import { UseCase, UseCaseAction } from "@/types/scripts";
import { apiFetch } from "@/lib/api";
import { AddDirectoryContacts, GetDirectory, RemoveDirectoryContact } from "@/utils/directory";

const BulkUploadDirectoryModal = dynamic(
  () => import("@/components/directory/BulkUploadDirectoryModal"),
  { ssr: false }
);

// Type for pending use case recommendation
interface PendingRecommendation {
  contactName: string;
  department: string;
  template: UseCaseTemplate;
}

// Suggested departments for quick setup
const SUGGESTED_DEPARTMENTS: { key: string; icon: IconNameExtended }[] = [
  { key: "sales", icon: "handshake" },
  { key: "support", icon: "headset" },
  { key: "billing", icon: "receipt" },
  { key: "claims", icon: "clipboard-list" },
];

// Use case templates for auto-recommendation
type UseCaseTemplate = {
  titleKey: string;
  descriptionKey: string;
  actions: { descriptionKey: string }[];
};

const USE_CASE_TEMPLATES: Record<string, UseCaseTemplate> = {
  sales: {
    titleKey: "configureGail.inbound.useCaseTemplates.sales.title",
    descriptionKey: "configureGail.inbound.useCaseTemplates.sales.description",
    actions: [
      {
        descriptionKey: "configureGail.inbound.useCaseTemplates.sales.action1",
      },
    ],
  },
  support: {
    titleKey: "configureGail.inbound.useCaseTemplates.support.title",
    descriptionKey: "configureGail.inbound.useCaseTemplates.support.description",
    actions: [
      {
        descriptionKey: "configureGail.inbound.useCaseTemplates.support.action1",
      },
    ],
  },
  billing: {
    titleKey: "configureGail.inbound.useCaseTemplates.billing.title",
    descriptionKey: "configureGail.inbound.useCaseTemplates.billing.description",
    actions: [
      {
        descriptionKey: "configureGail.inbound.useCaseTemplates.billing.action1",
      },
    ],
  },
  claims: {
    titleKey: "configureGail.inbound.useCaseTemplates.claims.title",
    descriptionKey: "configureGail.inbound.useCaseTemplates.claims.description",
    actions: [
      {
        descriptionKey: "configureGail.inbound.useCaseTemplates.claims.action1",
      },
    ],
  },
  default: {
    titleKey: "configureGail.inbound.useCaseTemplates.default.title",
    descriptionKey: "configureGail.inbound.useCaseTemplates.default.description",
    actions: [
      {
        descriptionKey: "configureGail.inbound.useCaseTemplates.default.action1",
      },
    ],
  },
};

interface NewContactForm {
  name: string;
  phone: string;
  department: string;
}

const INITIAL_FORM: NewContactForm = {
  name: "",
  phone: "",
  department: "",
};

// Generate unique ID for new phone numbers (must be a valid GUID for the API)
const generatePhoneId = () => crypto.randomUUID();

interface DirectorySetupSectionProps {
  readonly onUseCaseCreated?: (useCase: UseCase) => void;
}

export function DirectorySetupSection({ onUseCaseCreated }: DirectorySetupSectionProps = {}) {
  const { t } = useTranslation("onboarding");
  const { org } = useOrg();
  const { setIsSaving, setAlert } = useAlert();

  const [contacts, setContacts] = useState<AgencyDirectoryContact[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showAllContacts, setShowAllContacts] = useState(false);
  const [formData, setFormData] = useState<NewContactForm>(INITIAL_FORM);
  const [formErrors, setFormErrors] = useState<Partial<NewContactForm>>({});
  const [pendingRecommendation, setPendingRecommendation] = useState<PendingRecommendation | null>(
    null
  );
  const [isCreatingUseCase, setIsCreatingUseCase] = useState(false);

  // Fetch existing directory contacts
  useEffect(() => {
    const fetchContacts = async () => {
      setIsLoading(true);
      try {
        const response = await GetDirectory();
        if (response) {
          setContacts(response);
        }
      } catch (error) {
        console.error("Failed to fetch directory:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContacts();
  }, []);

  const validateForm = useCallback((): boolean => {
    const errors: Partial<NewContactForm> = {};

    if (!formData.name.trim()) {
      errors.name = t("configureGail.inbound.directory.errors.nameRequired");
    }

    if (!formData.phone.trim()) {
      errors.phone = t("configureGail.inbound.directory.errors.phoneRequired");
    } else if (formData.phone.replaceAll(/\D/g, "").length < 10) {
      errors.phone = t("configureGail.inbound.directory.errors.phoneInvalid");
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  const handleAddContact = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    // Check for duplicate phone number
    const normalizedPhone = formData.phone.replaceAll(/\D/g, "");
    const isDuplicate = contacts.some((c) =>
      c.phoneNumbers?.some((pn) => pn.phone?.replaceAll(/\D/g, "") === normalizedPhone)
    );

    if (isDuplicate) {
      setFormErrors((prev) => ({
        ...prev,
        phone: t("configureGail.inbound.directory.errors.phoneDuplicate"),
      }));
      return;
    }

    setIsSaving(true, t("configureGail.inbound.directory.saving"));

    const phoneNumber: AgencyDirectoryContactPhone = {
      id: generatePhoneId(),
      phone: formData.phone.replaceAll(/\D/g, ""),
      type: AgencyDirectoryPhoneType.Office,
    };

    const newContact: AgencyDirectoryContact = {
      id: "",
      type: formData.department ? "department" : "individual",
      firstName: formData.name,
      phoneNumbers: [phoneNumber],
      transfer: AgencyDirectoryTransferType.Always,
      department: formData.department || undefined,
    };

    try {
      const response = await AddDirectoryContacts([newContact], org);
      if (response) {
        setContacts(response);

        // Show use case recommendation after adding contact
        const templateKey = formData.department || "default";
        const template = USE_CASE_TEMPLATES[templateKey] || USE_CASE_TEMPLATES.default;
        setPendingRecommendation({
          contactName: formData.name,
          department: formData.department,
          template,
        });

        setFormData(INITIAL_FORM);
        setShowAddForm(false);
      }
    } catch (error) {
      console.error("Failed to add contact:", error);
      setAlert({
        status: "error",
        message: t("configureGail.inbound.directory.addContactFailed"),
        secondsToClose: 4,
      });
    } finally {
      setIsSaving(false, "");
    }
  }, [contacts, formData, org, setAlert, setIsSaving, t, validateForm]);

  const handleAcceptRecommendation = useCallback(async () => {
    if (!pendingRecommendation) return;

    setIsCreatingUseCase(true);

    const { template, contactName, department } = pendingRecommendation;
    const departmentLabel = department
      ? t(`configureGail.inbound.directory.departments.${department}`)
      : contactName;

    const newUseCase = {
      title: t(template.titleKey, { name: departmentLabel }),
      description: t(template.descriptionKey, { name: departmentLabel }),
      actions: template.actions.map((a) => ({
        description: t(a.descriptionKey, { name: departmentLabel }),
      })) as UseCaseAction[],
    };

    try {
      const response = await apiFetch<UseCase>(
        `${process.env.NEXT_PUBLIC_GAIL_API}/v1/use_cases`,
        "POST",
        {
        body: newUseCase,
      },
      );

      if (response) {
        onUseCaseCreated?.(response);
        setAlert({
          status: "success",
          message: t("configureGail.inbound.directory.useCaseCreated"),
          secondsToClose: 4,
        });
      }
    } catch (error) {
      console.error("Failed to create use case:", error);
      setAlert({
        status: "error",
        message: t("configureGail.inbound.directory.useCaseCreateFailed"),
      });
    } finally {
      setIsCreatingUseCase(false);
      setPendingRecommendation(null);
    }
  }, [pendingRecommendation, t, onUseCaseCreated, setAlert]);

  const handleSkipRecommendation = useCallback(() => {
    setPendingRecommendation(null);
  }, []);

  const handleQuickAdd = useCallback((departmentKey: string) => {
    setFormData({
      name: "",
      phone: "",
      department: departmentKey,
    });
    setShowAddForm(true);
  }, []);

  const handleFormChange = useCallback(
    (field: keyof NewContactForm, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear error when user starts typing
      if (formErrors[field]) {
        setFormErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    },
    [formErrors]
  );

  const handleRemoveContact = useCallback(
    async (contactId: string) => {
      setIsSaving(true, t("configureGail.inbound.directory.removing"));
      try {
        const response = await RemoveDirectoryContact(contactId);
        if (response) {
          setContacts(response);
        }
      } catch (error) {
        console.error("Failed to remove contact:", error);
      } finally {
        setIsSaving(false, "");
      }
    },
    [setIsSaving, t]
  );

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex items-center gap-3">
        <div
          className={clsx(
            "w-10 h-10 rounded-xl flex items-center justify-center",
            "bg-blue-100 dark:bg-blue-900/30"
          )}
        >
          <Icon icon="book-user" className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h3 className="font-semibold text-foreground">
            {t("configureGail.inbound.directory.title")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t("configureGail.inbound.directory.subtitle")}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className={clsx("rounded-2xl border border-200 bg-panel p-6 space-y-6")}>
        {/* Quick Add Suggestions */}
        {!showAddForm && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-700">
              {t("configureGail.inbound.directory.quickAdd")}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {SUGGESTED_DEPARTMENTS.map((dept) => (
                <button
                  key={dept.key}
                  type="button"
                  onClick={() => handleQuickAdd(dept.key)}
                  className={clsx(
                    "flex flex-col items-center gap-2 p-4 rounded-xl",
                    "border border-200 bg-50 dark:bg-800/50",
                    "hover:border-primary/50 hover:bg-primary/5",
                    "transition-all duration-200"
                  )}
                >
                  <Icon icon={dept.icon} className="w-6 h-6 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    {t(`configureGail.inbound.directory.departments.${dept.key}`)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Add Contact Form */}
        {showAddForm && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-700">
                {formData.department
                  ? t("configureGail.inbound.directory.addDepartment", {
                      department: t(
                        `configureGail.inbound.directory.departments.${formData.department}`
                      ),
                    })
                  : t("configureGail.inbound.directory.addContact")}
              </p>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setFormData(INITIAL_FORM);
                  setFormErrors({});
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon icon="xmark" className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                type="text"
                label={t("configureGail.inbound.directory.nameLabel")}
                placeholder={t("configureGail.inbound.directory.namePlaceholder")}
                value={formData.name}
                onChange={(e) => handleFormChange("name", e.target.value)}
                error={!!formErrors.name}
                helperText={formErrors.name}
              />
              <Input
                label={t("configureGail.inbound.directory.phoneLabel")}
                placeholder={t("configureGail.inbound.directory.phonePlaceholder")}
                type="phone"
                value={formData.phone}
                onChange={(e) => handleFormChange("phone", e.target.value)}
                error={!!formErrors.phone}
                helperText={formErrors.phone}
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type="tertiary"
                onClick={() => {
                  setShowAddForm(false);
                  setFormData(INITIAL_FORM);
                  setFormErrors({});
                }}
              >
                {t("configureGail.inbound.directory.cancel")}
              </Button>
              <Button
                type="primary"
                onClick={handleAddContact}
                iconBefore={<Icon icon="plus" className="w-4 h-4" />}
              >
                {t("configureGail.inbound.directory.add")}
              </Button>
            </div>
          </div>
        )}

        {/* Add Buttons */}
        {!showAddForm && (
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setShowAddForm(true)}
              className={clsx(
                "flex items-center justify-center gap-2 p-3 rounded-xl",
                "border-2 border-dashed border-300 dark:border-600",
                "text-muted-foreground hover:text-foreground",
                "hover:border-primary/50 hover:bg-primary/5",
                "transition-all duration-200"
              )}
            >
              <Icon icon="plus" className="w-4 h-4" />
              <span className="text-sm font-medium">
                {t("configureGail.inbound.directory.addCustom")}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setShowBulkUpload(true)}
              className={clsx(
                "flex items-center justify-center gap-2 p-3 rounded-xl",
                "border-2 border-dashed border-300 dark:border-600",
                "text-muted-foreground hover:text-foreground",
                "hover:border-primary/50 hover:bg-primary/5",
                "transition-all duration-200"
              )}
            >
              <Icon icon="upload" className="w-4 h-4" />
              <span className="text-sm font-medium">
                {t("configureGail.inbound.directory.bulkUpload")}
              </span>
            </button>
          </div>
        )}

        {/* Existing Contacts */}
        {contacts.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-700">
                {t("configureGail.inbound.directory.existingContacts", {
                  count: contacts.length,
                })}
              </p>
              {contacts.length > 5 && (
                <button
                  type="button"
                  onClick={() => setShowAllContacts(!showAllContacts)}
                  className={clsx(
                    "flex items-center gap-1.5 text-sm text-primary",
                    "hover:text-primary/80 transition-colors"
                  )}
                >
                  <span>
                    {showAllContacts
                      ? t("configureGail.inbound.directory.showLess")
                      : t("configureGail.inbound.directory.showAll", {
                          count: contacts.length,
                        })}
                  </span>
                  {showAllContacts ? (
                    <Icon icon="chevron-up" className="w-3 h-3" />
                  ) : (
                    <Icon icon="chevron-down" className="w-3 h-3" />
                  )}
                </button>
              )}
            </div>
            <div className="space-y-2">
              {(showAllContacts ? contacts : contacts.slice(0, 5)).map((contact) => (
                <div
                  key={contact.id}
                  className={clsx(
                    "flex items-center gap-3 p-3 rounded-lg",
                    "bg-50 dark:bg-800/50 border border-200"
                  )}
                >
                  <div
                    className={clsx(
                      "w-8 h-8 rounded-full flex items-center justify-center",
                      "bg-primary/10 text-primary"
                    )}
                  >
                    {contact.type === "department" ? (
                      <Icon icon="building" className="w-4 h-4" />
                    ) : (
                      <Icon icon="user" className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {contact.nickname ||
                        contact.firstName ||
                        contact.department ||
                        t("configureGail.inbound.directory.unknownContact")}
                    </p>
                    {contact.phoneNumbers?.[0]?.phone && (
                      <p className="text-xs text-muted-foreground">
                        {contact.phoneNumbers[0].phone}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveContact(contact.id)}
                    className={clsx(
                      "p-1.5 rounded-full flex-shrink-0",
                      "text-muted-foreground hover:text-destructive",
                      "hover:bg-destructive/10 transition-colors"
                    )}
                    title={t("configureGail.inbound.directory.removeContact")}
                  >
                    <Icon icon="trash" className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            {!showAllContacts && contacts.length > 5 && (
              <button
                type="button"
                onClick={() => setShowAllContacts(true)}
                className={clsx(
                  "w-full flex items-center justify-center gap-2 py-2 rounded-lg",
                  "text-sm text-muted-foreground hover:text-foreground",
                  "hover:bg-50 dark:hover:bg-800/50 transition-colors"
                )}
              >
                <span>
                  {t("configureGail.inbound.directory.moreContacts", {
                    count: contacts.length - 5,
                  })}
                </span>
                <Icon icon="chevron-down" className="w-3 h-3" />
              </button>
            )}
          </div>
        )}

        {/* Loading state - use CSS spinner to avoid DOM issues */}
        {isLoading && (
          <div className="flex items-center justify-center py-4">
            <div className="w-8 h-8 border-3 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        )}

        {/* Use Case Recommendation */}
        {pendingRecommendation && (
          <div className={clsx("rounded-xl border-2 border-primary/30 bg-primary/5 p-4 space-y-4")}>
            <div className="flex items-start gap-3">
              <div
                className={clsx(
                  "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                  "bg-primary/20 text-primary"
                )}
              >
                <Icon icon="lightbulb" className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-foreground">
                  {t("configureGail.inbound.directory.recommendation.title")}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {t("configureGail.inbound.directory.recommendation.description", {
                    name: pendingRecommendation.department
                      ? t(
                          `configureGail.inbound.directory.departments.${pendingRecommendation.department}`
                        )
                      : pendingRecommendation.contactName,
                  })}
                </p>
              </div>
            </div>

            {/* Preview of suggested use case */}
            <div className={clsx("rounded-lg border border-200 bg-panel p-4 space-y-2")}>
              <div className="flex items-center gap-2">
                <Icon icon="list-check" className="w-4 h-4 text-primary" />
                <p className="font-medium text-foreground text-sm">
                  {t(pendingRecommendation.template.titleKey, {
                    name: pendingRecommendation.department
                      ? t(
                          `configureGail.inbound.directory.departments.${pendingRecommendation.department}`
                        )
                      : pendingRecommendation.contactName,
                  })}
                </p>
              </div>
              <p className="text-sm text-muted-foreground ml-6">
                {t(pendingRecommendation.template.descriptionKey, {
                  name: pendingRecommendation.department
                    ? t(
                        `configureGail.inbound.directory.departments.${pendingRecommendation.department}`
                      )
                    : pendingRecommendation.contactName,
                })}
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                type="tertiary"
                onClick={handleSkipRecommendation}
                disabled={isCreatingUseCase}
              >
                {t("configureGail.inbound.directory.recommendation.skip")}
              </Button>
              <Button
                type="primary"
                onClick={handleAcceptRecommendation}
                disabled={isCreatingUseCase}
              >
                {isCreatingUseCase ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t("configureGail.inbound.directory.recommendation.creating")}
                  </span>
                ) : (
                  t("configureGail.inbound.directory.recommendation.accept")
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Info hint */}
        <div
          className={clsx(
            "flex items-center gap-2 p-3 rounded-lg",
            "bg-100 dark:bg-800/60 border border-200"
          )}
        >
          <Icon icon="lightbulb" className="w-4 h-4 text-amber-500 flex-shrink-0" />
          <p className="text-xs text-muted-foreground">
            {t("configureGail.inbound.directory.hint")}
          </p>
        </div>
      </div>

      {/* Bulk Upload Modal */}
      <BulkUploadDirectoryModal
        isUploaderOpen={showBulkUpload}
        setIsUploaderOpen={setShowBulkUpload}
        existingContacts={contacts}
        onUploadComplete={(saved) => {
          setContacts((prev) => [...prev, ...saved]);
        }}
      />
    </div>
  );
}
